// src/contexts/ColorModeProvider.tsx
import React, {
    createContext,
    useMemo,
    useState,
    useContext,
    useEffect,
} from "react";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { getAppTheme } from "../utils/themeOverrides";
import { useUserPreferences } from "./UserPreferencesContext";

type ColorModeContextType = {
    mode: "light" | "dark";
    toggleColorMode: () => void;
};

const ColorModeContext = createContext<ColorModeContextType | undefined>(
    undefined
);

// Helper to detect system theme preference
const getSystemTheme = (): "light" | "dark" => {
    if (typeof window !== 'undefined' && window.matchMedia) {
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light';
};

export const ColorModeProvider = ({ children }: { children: React.ReactNode }) => {
    const { preferences, isLoaded, setTheme } = useUserPreferences();
    const [mode, setMode] = useState<"light" | "dark">(() => {
        // Initialize with system preference
        return getSystemTheme();
    });

    // Load theme from preferences once they're loaded
    useEffect(() => {
        if (isLoaded) {
            if (preferences.theme) {
                setMode(preferences.theme);
            } else {
                // No saved preference, use system preference
                const systemTheme = getSystemTheme();
                setMode(systemTheme);
                setTheme(systemTheme);
            }
        }
    }, [isLoaded, preferences.theme, setTheme]);

    const colorMode = useMemo(
        () => ({
            mode,
            toggleColorMode: () => {
                setMode((prev) => {
                    const newMode = prev === "light" ? "dark" : "light";
                    setTheme(newMode); // Save to preferences
                    return newMode;
                });
            },
        }),
        [mode, setTheme]
    );

    const theme = useMemo(() => getAppTheme(mode), [mode]);

    useEffect(() => {
        document.body.setAttribute("data-theme", mode);
    }, [mode]);

    return (
        <ColorModeContext.Provider value={colorMode}>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                {children}
            </ThemeProvider>
        </ColorModeContext.Provider>
    );
};

export const useColorMode = () => {
    const context = useContext(ColorModeContext);
    if (!context) throw new Error("useColorMode must be used within ColorModeProvider");
    return context;
};
